<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="user-query-page">
            <!-- 搜索栏 -->
            <div class="search-section">
                <van-search v-model="searchKeyword" placeholder="输入用户名或ID搜索" @search="handleSearch" @clear="handleClear"
                    class="search-bar" />
            </div>
            <!-- 空数据 -->
            <div class="empty-data" v-if="users.length === 0">
                <van-empty description="暂无数据" />
            </div>

            <!-- 用户列表 -->
            <template v-else>
                <div class="user-list">
                    <div v-for="user in users" :key="user.id" class="user-item">
                        <div class="user-info">
                            <div class="user-name">{{ user.name }}</div>
                            <div class="user-id">ID: {{ user.wx_user_code }}</div>
                            <div class="user-status">
                                <span class="user-role" v-if="user.is_student">学生</span>
                                <span class="user-role" v-if="user.is_teacher">老师</span>
                                <span class="user-role" v-if="user.is_parent">家长</span>
                                <span class="user-role" v-if="user.is_agent">业务员</span>
                                <span class="user-role" v-if="user.is_principal">校长</span>
                                <span class="user-role" v-if="user.is_customer">社区客户</span>
                                <span class="user-role" v-if="user.is_collect">采集员</span>
                                <van-tag :color="Object.keys(user.face_image_arr || {}).length > 0 ? '#DCFCE7' : '#FEF9C3'"
                                    :text-color="Object.keys(user.face_image_arr || {}).length > 0 ? '#16A34A' : '#CA8A04'"
                                    class="face-status-tag">
                                    {{ Object.keys(user.face_image_arr || {}).length > 0 ? '已录脸' : '未录脸' }}
                                </van-tag>
                            </div>
                        </div>
                        <div class="user-actions">
                            <van-button type="primary" size="small" round class="modify-btn" @click="modifyUser(user)">
                                修改
                            </van-button>
                        </div>
                    </div>
                </div>
            </template>

            <!-- 修改用户弹框 -->
            <van-popup v-model:show="showModifyModal" position="center" round :style="{ width: '90%' }">
                <div class="modify-modal">
                    <div class="modal-header">
                        <span class="modal-title">修改用户信息</span>
                    </div>

                    <div class="modal-content">
                        <div class="input-group">
                            <div class="input-label">用户名称</div>
                            <van-field v-model="modifyForm.name" placeholder="请输入用户名称" class="modify-input" />
                        </div>

                        <div class="input-group">
                            <div class="input-label">用户ID</div>
                            <van-field v-model="modifyForm.wx_user_code" placeholder="请输入用户ID" disabled class="modify-input" />
                        </div>

                        <div class="input-group">
                            <div class="input-label">人脸录入状态</div>
                            <div class="face-status-display">
                                <van-tag :color="modifyForm.faceStatus === '已录脸' ? '#F0FDF4' : '#fefce8'"
                                    :text-color="modifyForm.faceStatus === '已录脸' ? '#16A34A' : '#CA8A04'" round
                                    class="face-status-tag">
                                    <van-icon :name="modifyForm.faceStatus === '已录脸' ? 'checked' : 'warning'" />
                                    {{ modifyForm.faceStatus }}
                                </van-tag>
                            </div>
                        </div>

                        <div class="input-group">
                            <div class="input-label">社区限购瓶数</div>
                            <van-field v-model="modifyForm.quota_num" placeholder="请输入社区限购瓶数" type="number"
                                class="modify-input" />
                        </div>

                        <div class="input-group">
                            <div class="input-label">学校限购瓶数</div>
                            <van-field v-model="modifyForm.school_quota_num" placeholder="请输入学校限购瓶数" type="number"
                                class="modify-input" />
                        </div>
                    </div>

                    <div class="modal-actions">
                        <van-button v-if="modifyForm.faceStatus === '已录脸'" type="danger" round plain
                            class="delete-face-btn" @click="deleteFaceInfo">
                            <van-icon name="delete" />
                            删除人脸信息
                        </van-button>
                    </div>

                    <div class="modal-footer">
                        <van-button class="cancel-btn" round @click="showModifyModal = false">取消</van-button>
                        <van-button class="save-btn" type="primary" round @click="confirmModify"
                            :disabled="!isModifyFormValid">保存</van-button>
                    </div>
                </div>
            </van-popup>
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import PageLoading from '@/components/PageLoading.vue'
import ErrorPage from '@/components/ErrorPage.vue'
import { post } from '@/utils/request'
import { Dialog } from 'vant'

onMounted(() => {
    setWxTitle('查询修改')
})

// 加载状态
const loading = ref(false)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 响应式数据
const searchKeyword = ref('')
const showModifyModal = ref(false)

// 修改表单数据
const modifyForm = ref({})

// 用户数据
const users = ref([])

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

// 页面初始化
const initPage = async () => {
    loading.value = true
    try {
        if (!searchKeyword.value) {
            return
        }
        // 这里可以调用API获取用户数据
        const params = {
            company_ids: JSON.parse(localStorage.getItem('company_ids')),
            sale_id: parseInt(localStorage.getItem('userId'))
        }
        if (searchKeyword.value) {
            params.name_and_code = searchKeyword.value
        }
        const res = await post('/api/select/partner/sale/place', params)
        if (res.result && res.result.state === 'ok') {
            users.value = res.result.partner_arr || []
        } else {
            Dialog.alert({
                title: '加载失败',
                message: res.result?.message || '接口异常'
            })
        }
    } catch (error) {
        console.error('页面初始化失败:', error)
        Dialog.alert({
            title: '加载失败',
            message: '页面加载失败，请重试'
        })
    } finally {
        loading.value = false
    }
}

// 方法
const handleSearch = () => {
    console.log('搜索关键词:', searchKeyword.value)
    initPage()
}

const handleClear = () => {
    searchKeyword.value = ''
}

// 修改表单验证
const isModifyFormValid = computed(() => {
    return modifyForm.value.name.trim()
})

const modifyUser = (user) => {
    modifyForm.value = JSON.parse(JSON.stringify(user))
    modifyForm.value.faceStatus = Object.keys(user.face_image_arr || {}).length > 0 ? '已录脸' : '未录脸'
    showModifyModal.value = true
}

const confirmModify = async () => {
    if (!isModifyFormValid.value) {
        return
    }
    loading.value = true
    try {
        const params = {
            partner_id: modifyForm.value.id,
            update_partner_id: parseInt(localStorage.getItem('userId')),
            name: modifyForm.value.name,
            school_quota_num: modifyForm.value.school_quota_num,
            quota_num: modifyForm.value.quota_num,
        }
        if (modifyForm.value.faceStatus === '未录脸') {
            params.is_delete_face = '是'
        }
        const res = await post('/api/select/partner/sale/person', params)
        if (res.result && res.result.state === 'ok') {
            initPage()
        } else {
            Dialog.alert({
                title: '修改失败',
                message: res.result?.message || '接口异常'
            })
        }
    } catch (error) {
        console.error('修改用户信息失败:', error)
        Dialog.alert({
            title: '修改失败',
            message: '页面加载失败，请重试'
        })
    } finally {
        loading.value = false
        showModifyModal.value = false
    }
}

const deleteFaceInfo = () => {
    modifyForm.value.faceStatus = '未录脸'
}

</script>

<style scoped>
.user-query-page {
    background: #fff;
    min-height: calc(100vh - 16px);
    padding: 0 16px 16px 16px;
}

.search-section {
    padding: 16px 16px 24px 16px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    background: #fff;
}

.search-bar {
    border-radius: 8px;
    border: 1px solid #E5E7EB;
    color: #6B7280;
    padding: 0;
}

:deep(.van-search__content) {
    background: transparent;
    padding: 5px 11px;
}

.user-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 70px;
}

.user-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-radius: 8px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.user-info {
    flex: 1;
}

.user-name {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 4px;
}

.user-id {
    font-size: 14px;
    color: #6B7280;
    margin-bottom: 4px;
}

.user-status {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.user-role {
    font-size: 14px;
    color: #6B7280;
}

.face-status-tag {
    font-size: 12px;
    padding: 3px 8px;
}

.user-actions {
    margin-left: auto;
}

.modify-btn {
    padding: 7px 14px;
    font-size: 14px;
    border-radius: 8px;
}

/* 修改弹框样式 */
.modify-modal {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    padding: 21px;
}

.modal-header {
    margin-bottom: 21px;
}

.modal-title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
}

.modal-content {
    margin-bottom: 21px;
}

.modal-actions {
    margin-bottom: 21px;
    text-align: center;
}

.delete-face-btn {
    height: 34px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    width: 100%;
    border-radius: 8px;
}

.input-group {
    margin-bottom: 14px;
}

.input-group:last-child {
    margin-bottom: 0;
}

.input-label {
    font-size: 12px;
    color: #4b5563;
    margin-bottom: 4px;
}

.modify-input {
    font-size: 12px;
    border-radius: 7px;
    border: 1px solid #e5e7eb;
    padding: 9px;
    line-height: unset;
}

.face-status-display {
    margin-top: 8px;
}

.face-status-display .face-status-tag {
    font-size: 14px;
    padding: 8px 12px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
}

.face-status-display .face-status-tag .van-icon {
    font-size: 18px;
}

.modal-footer {
    display: flex;
    gap: 12px;
    margin-top: 21px;
}

.cancel-btn,
.save-btn {
    flex: 1;
    height: 38px;
    font-size: 14px;
    border: none;
}

.cancel-btn {
    background: #f3f4f6;
    color: #4b5563;
}

.empty-data {
    padding-top: 60px;
}
</style>